import { Badge, Button, cn, Skeleton } from "@devhop/ui";
import { Download, FileText } from "lucide-react";
import { useMemo } from "react";
import type { JudgeRoleId } from "@/modules/events/presentation/api/client";
import {
  useAdminJudgeProfilesQuery,
  useEventSummaryQuery,
} from "@/modules/events/presentation/api/queries";
import type { TeamResult } from "../api/client";
import { useResultsQuery } from "../api/queries";
import { exportScoringReportToExcel } from "../lib/exportScoringReport";

type SubCriterion = {
  label: string;
  max: number;
  read: (team: TeamResult) => number;
};

type JudgeReportMeta = {
  role: JudgeRoleId;
  judgeNum: 1 | 2 | 3;
  title: string;
  subject: string;
  accentClass: string;
  subCriteria: SubCriterion[];
};

const JUDGE_REPORT_META: JudgeReportMeta[] = [
  {
    role: "judge1",
    judgeNum: 1,
    title: "Judge 1",
    subject: "Dance Skill & Technique",
    accentClass: "text-accent",
    subCriteria: [
      {
        label: "Strength & Difficulty",
        max: 10,
        read: (t) => t.judge1.strengthDifficulty,
      },
      {
        label: "Timing & Control",
        max: 10,
        read: (t) => t.judge1.timingControl,
      },
      {
        label: "Unity & Synchronizity",
        max: 10,
        read: (t) => t.judge1.unitySynchronicity,
      },
    ],
  },
  {
    role: "judge2",
    judgeNum: 2,
    title: "Judge 2",
    subject: "Stage Presence & Performance",
    accentClass: "text-chart-3",
    subCriteria: [
      {
        label: "Charisma & Confidence",
        max: 10,
        read: (t) => t.judge2.charismaConfidence,
      },
      {
        label: "Facial Expression & Emotions",
        max: 10,
        read: (t) => t.judge2.facialExpression,
      },
      {
        label: "Stage Awareness & Engagement",
        max: 10,
        read: (t) => t.judge2.stageAwareness,
      },
    ],
  },
  {
    role: "judge3",
    judgeNum: 3,
    title: "Judge 3",
    subject: "Concept & Creativity",
    accentClass: "text-chart-5",
    subCriteria: [
      {
        label: "Concept & Originality",
        max: 15,
        read: (t) => t.judge3.conceptOriginality,
      },
      {
        label: "Costume, Props & Visual Composition",
        max: 15,
        read: (t) => t.judge3.costumeVisual,
      },
      {
        label: "Choreography Creativity & Overall Quality",
        max: 10,
        read: (t) => t.judge3.choreographyQuality,
      },
    ],
  },
];

function judgeSubmitted(team: TeamResult, role: JudgeRoleId): boolean {
  if (role === "judge1") return team.judge1.submitted;
  if (role === "judge2") return team.judge2.submitted;
  return team.judge3.submitted;
}

/** Extra criteria that belong to a given judge, ordered as returned by API. */
function extraColumnsForRound(
  teams: TeamResult[],
  role: JudgeRoleId,
): { criterionId: string; name: string; maxScore: number }[] {
  const seen = new Map<
    string,
    { criterionId: string; name: string; maxScore: number }
  >();
  for (const team of teams) {
    for (const c of team.extraCriteria) {
      if (c.judgeRole === role && !seen.has(c.criterionId)) {
        seen.set(c.criterionId, {
          criterionId: c.criterionId,
          name: c.name,
          maxScore: c.maxScore,
        });
      }
    }
  }
  return [...seen.values()];
}

function ScoreValue({
  value,
  max,
  muted,
}: {
  value: number;
  max: number;
  muted?: boolean;
}) {
  return (
    <span className={cn("tabular-nums", muted && "text-muted-foreground")}>
      <span className="font-semibold">{value}</span>
      <span className="text-muted-foreground text-xs">/{max}</span>
    </span>
  );
}

function JudgeRoundTable({
  meta,
  teams,
}: {
  meta: JudgeReportMeta;
  teams: TeamResult[];
}) {
  const extraColumns = useMemo(
    () => extraColumnsForRound(teams, meta.role),
    [teams, meta.role],
  );

  const baseMax = meta.subCriteria.reduce((s, c) => s + c.max, 0);
  const extraMax = extraColumns.reduce((s, c) => s + c.maxScore, 0);
  const judgeMax = baseMax + extraMax;

  const ordered = useMemo(
    () => [...teams].sort((a, b) => a.orderNumber - b.orderNumber),
    [teams],
  );

  return (
    <div className="overflow-x-auto rounded-xl ring-1 ring-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-border border-b bg-muted/40 text-left text-muted-foreground">
            <th className="sticky left-0 z-10 bg-muted/40 px-4 py-3 font-medium">
              ທີມ
            </th>
            {meta.subCriteria.map((c) => (
              <th
                key={c.label}
                className="px-3 py-3 text-center font-medium"
                title={c.label}
              >
                <span className="block max-w-30 truncate">{c.label}</span>
                <span className="font-normal text-xs">/{c.max}</span>
              </th>
            ))}
            {extraColumns.map((c) => (
              <th
                key={c.criterionId}
                className="bg-primary/5 px-3 py-3 text-center font-medium text-primary"
                title={`${c.name} (พิเศษ)`}
              >
                <span className="block max-w-30 truncate">★ {c.name}</span>
                <span className="font-normal text-xs">/{c.maxScore}</span>
              </th>
            ))}
            <th className="px-4 py-3 text-center font-medium">
              ລວມ {meta.title}
              <span className="block font-normal text-xs">/{judgeMax}</span>
            </th>
            <th className="px-3 py-3 text-center font-medium">ສະຖານະ</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((team, idx) => {
            const submitted = judgeSubmitted(team, meta.role);
            const baseTotal = meta.subCriteria.reduce(
              (s, c) => s + c.read(team),
              0,
            );
            const extraByCriterion = new Map(
              team.extraCriteria.map((c) => [c.criterionId, c.value] as const),
            );
            const extraTotal = extraColumns.reduce(
              (s, c) => s + (extraByCriterion.get(c.criterionId) ?? 0),
              0,
            );
            const judgeTotal = baseTotal + extraTotal;
            return (
              <tr
                key={team.id}
                className={cn(
                  "border-border border-b last:border-0",
                  idx % 2 === 0 ? "bg-card" : "bg-muted/20",
                )}
              >
                <td className="sticky left-0 z-10 bg-inherit px-4 py-3 font-medium text-foreground">
                  <span className="mr-2 text-muted-foreground text-xs tabular-nums">
                    #{team.orderNumber}
                  </span>
                  {team.name}
                </td>
                {meta.subCriteria.map((c) => (
                  <td key={c.label} className="px-3 py-3 text-center">
                    <ScoreValue
                      value={c.read(team)}
                      max={c.max}
                      muted={!submitted}
                    />
                  </td>
                ))}
                {extraColumns.map((c) => (
                  <td
                    key={c.criterionId}
                    className="bg-primary/5 px-3 py-3 text-center"
                  >
                    <ScoreValue
                      value={extraByCriterion.get(c.criterionId) ?? 0}
                      max={c.maxScore}
                      muted={!submitted}
                    />
                  </td>
                ))}
                <td className="px-4 py-3 text-center font-bold text-foreground tabular-nums">
                  {judgeTotal}
                  <span className="font-normal text-muted-foreground text-xs">
                    /{judgeMax}
                  </span>
                </td>
                <td className="px-3 py-3 text-center">
                  {submitted ? (
                    <Badge variant="secondary">ສົ່ງແລ້ວ</Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">
                      ຍັງ
                    </Badge>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ScoringReportTab({
  eventId,
  eventName,
  eventSlug,
}: {
  eventId: string;
  eventName: string;
  eventSlug: string;
}) {
  const { data: results, isLoading } = useResultsQuery(eventId);
  const { data: profiles } = useAdminJudgeProfilesQuery(eventId);
  const { data: eventMeta } = useEventSummaryQuery(eventId);

  const displayNameByRole = useMemo(() => {
    const m = new Map<JudgeRoleId, string>();
    for (const p of profiles ?? []) {
      if (p.displayName?.trim()) m.set(p.role, p.displayName.trim());
    }
    return m;
  }, [profiles]);

  const hasAnyTeams = results?.divisions.some((s) =>
    s.rounds.some((r) => r.teams.length > 0),
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-6">
        {Array.from({ length: 5 }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (!hasAnyTeams) {
    return (
      <div className="py-16 text-center">
        <FileText className="mx-auto mb-3 size-10 text-muted-foreground" />
        <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີຄະແນນໃຫ້ລາຍງານ</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-semibold text-foreground">ລາຍງານການໃຫ້ຄະແນນ</p>
          <p className="text-muted-foreground text-xs leading-relaxed">
            ແຍກຕາມກຳມະການ — ໃຜໃຫ້ຄະແນນຫົວຂໍ້ໃດ ໃຫ້ທີມໃດ ເທົ່າໃດ (ລວມ criteria ພິເສດ)
          </p>
        </div>
        {results ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              exportScoringReportToExcel(results, {
                eventName: eventMeta?.name ?? eventName,
                eventSlug: eventMeta?.slug ?? eventSlug,
              })
            }
          >
            <Download data-icon="inline-start" />
            Export ລາຍງານ
          </Button>
        ) : null}
      </div>

      {JUDGE_REPORT_META.map((meta) => {
        const judgeName = displayNameByRole.get(meta.role);
        return (
          <section key={meta.role} className="flex flex-col gap-4">
            <div className="flex items-baseline gap-2 border-border border-b pb-2">
              <h3
                className={cn(
                  "font-bold text-base tracking-tight",
                  meta.accentClass,
                )}
              >
                {meta.title}
              </h3>
              {judgeName ? (
                <span className="font-medium text-foreground text-sm">
                  · {judgeName}
                </span>
              ) : null}
              <span className="text-muted-foreground text-xs">
                — {meta.subject}
              </span>
            </div>

            {results?.divisions.map((section) => {
              const sectionHasTeams = section.rounds.some(
                (r) => r.teams.length > 0,
              );
              if (!sectionHasTeams) return null;
              return (
                <div key={section.divisionId} className="flex flex-col gap-3">
                  <h4 className="font-semibold text-foreground text-sm">
                    {section.divisionName}
                  </h4>
                  {section.rounds.map((rnd) =>
                    rnd.teams.length === 0 ? null : (
                      <div
                        key={`${meta.role}-${section.divisionId}-r${rnd.roundIndex}`}
                        className="flex flex-col gap-2"
                      >
                        {section.roundCount > 1 ? (
                          <p className="font-medium text-muted-foreground text-xs">
                            ຮອບ {rnd.roundIndex}{" "}
                            <span className="tabular-nums">
                              ({rnd.teams.length} ທີມ)
                            </span>
                          </p>
                        ) : null}
                        <JudgeRoundTable meta={meta} teams={rnd.teams} />
                      </div>
                    ),
                  )}
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}
